import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Save, Eye, Mail, RotateCcw, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const FIELDS = ['subject', 'video_url', 'video_button_label', 'cuerpo', 'cierre', 'kicker', 'tagline', 'video_title'];

export function UltimaCapacitacionEditor({ slug = 'ultima_capacitacion', onContentSaved, onDeleted }) {
    const BASE = `/admin/comunicados/${slug}`;
    const [content, setContent] = useState(null);
    const [title, setTitle] = useState('');
    const [testStatus, setTestStatus] = useState(null);
    const [meta, setMeta] = useState({});
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewDevice, setPreviewDevice] = useState('desktop');
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [history, setHistory] = useState([]);
    const [tracking, setTracking] = useState({ configurado: false, eventos: 0 });
    const [engagement, setEngagement] = useState([]);
    const [openBatch, setOpenBatch] = useState(null);

    const loadContent = async () => {
        const { data } = await api.get(`${BASE}/content`);
        setContent(data.content);
        setTitle(data.title || '');
        setMeta({ updated_at: data.updated_at, updated_by: data.updated_by, defaults: data.defaults });
    };
    const loadTestStatus = async () => {
        try { const { data } = await api.get(`${BASE}/test-status`); setTestStatus(data); } catch { /* opcional */ }
    };
    useEffect(() => {
        loadTestStatus();
        const id = setInterval(loadTestStatus, 15000);
        return () => clearInterval(id);
    }, [BASE]);
    const loadHistory = async () => {
        const [{ data }, eng] = await Promise.all([
            api.get(`/admin/comunicados/envios?mode=${slug}`),
            api.get('/admin/comunicados/engagement'),
        ]);
        setHistory(data.batches || []);
        setTracking({ configurado: data.tracking_configurado, eventos: data.eventos_recibidos || 0 });
        setEngagement(eng.data.templates || []);
    };
    useEffect(() => { loadContent().catch(() => toast.error('No se pudo cargar el contenido')); loadHistory().catch(() => {}); }, []);

    const dirty = content && meta.saved && FIELDS.some((f) => (content[f] || '') !== (meta.saved[f] || ''));
    useEffect(() => { if (content && !meta.saved) setMeta((m) => ({ ...m, saved: { ...content } })); }, [content, meta.saved]);

    const set = (k) => (e) => setContent((c) => ({ ...c, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`${BASE}/content`, { ...content, title });
            setContent(data.content);
            setMeta((m) => ({ ...m, updated_at: data.updated_at, updated_by: data.updated_by, saved: { ...data.content } }));
            toast.success('Contenido guardado');
            onContentSaved?.(data.content);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo guardar');
        } finally { setSaving(false); }
    };

    const doPreview = async () => {
        setLoadingPreview(true);
        try {
            const { data } = await api.post(`${BASE}/preview`, content);
            setPreview(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo generar la vista previa');
        } finally { setLoadingPreview(false); }
    };

    const sendTest = async () => {
        if (!testEmail.includes('@')) { toast.error('Indica un correo válido'); return; }
        if (dirty && !window.confirm('Hay cambios sin guardar. La prueba se envía con el contenido GUARDADO. ¿Continuar?')) return;
        setSendingTest(true);
        try {
            const { data } = await api.post(`${BASE}/send-test`, { email: testEmail });
            data.ok ? toast.success(`Prueba enviada a ${data.to}`) : toast.error(`Falló la prueba: ${data.reason || 'sin detalle'}`);
            await loadTestStatus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al enviar la prueba');
        } finally { setSendingTest(false); }
    };

    const restore = () => { if (window.confirm('¿Restaurar el texto original del comunicado? (no se guarda hasta pulsar Guardar)')) setContent({ ...meta.defaults }); };

    if (!content) return <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando editor…</div>;

    return (
        <div className="space-y-4 rounded-lg border border-red-200 bg-red-50/40 p-4" data-testid="ultima-capacitacion-editor">
            <div className="text-xs text-red-900">
                Modo <strong>{title || slug}</strong>: comunicado <strong>editable</strong>. Edita asunto, enlace del video y textos, guarda, previsualiza el correo real y envía una prueba antes del envío definitivo. Envío único por docente (no se re-envía a quien ya lo recibió). <strong>Nunca se envía automáticamente.</strong>
                {meta.updated_at && <span className="block mt-1 text-red-700/80">Última edición guardada: {new Date(meta.updated_at).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} · {meta.updated_by}</span>}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs font-medium text-slate-700">Nombre interno del comunicado (solo en este panel)
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-white" data-testid="uc-title-input" />
                </label>
                <label className="text-xs font-medium text-slate-700">Antetítulo (franja superior del correo)
                    <Input value={content.kicker || ''} onChange={set('kicker')} className="mt-1 bg-white" data-testid="uc-kicker-input" />
                </label>
                <label className="text-xs font-medium text-slate-700 md:col-span-2">Asunto (también es el título del correo)
                    <Input value={content.subject} onChange={set('subject')} className="mt-1 bg-white" data-testid="uc-subject-input" />
                </label>
                <label className="text-xs font-medium text-slate-700 md:col-span-2">Subtítulo bajo el asunto
                    <Input value={content.tagline || ''} onChange={set('tagline')} className="mt-1 bg-white" data-testid="uc-tagline-input" />
                </label>
                <label className="text-xs font-medium text-slate-700">Enlace del video (YouTube) · vacío = sin bloque de video
                    <Input value={content.video_url || ''} onChange={set('video_url')} className="mt-1 bg-white" data-testid="uc-video-url-input" />
                </label>
                <label className="text-xs font-medium text-slate-700">Título del bloque de video
                    <Input value={content.video_title || ''} onChange={set('video_title')} className="mt-1 bg-white" data-testid="uc-video-title-input" />
                </label>
                <label className="text-xs font-medium text-slate-700">Texto del botón del video
                    <Input value={content.video_button_label} onChange={set('video_button_label')} className="mt-1 bg-white" data-testid="uc-video-label-input" />
                </label>
                <label className="text-xs font-medium text-slate-700 md:col-span-2">Cuerpo (antes de los botones) · las líneas que empiezan con ✅ se muestran como lista
                    <Textarea value={content.cuerpo} onChange={set('cuerpo')} rows={12} className="mt-1 bg-white text-sm" data-testid="uc-cuerpo-textarea" />
                </label>
                <label className="text-xs font-medium text-slate-700 md:col-span-2">Cierre (después de los botones)
                    <Textarea value={content.cierre} onChange={set('cierre')} rows={5} className="mt-1 bg-white text-sm" data-testid="uc-cierre-textarea" />
                </label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={save} disabled={saving} className="gap-1.5 bg-red-700 hover:bg-red-800" data-testid="uc-save-btn">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Guardar contenido{dirty ? ' *' : ''}
                </Button>
                <Button size="sm" variant="outline" onClick={doPreview} disabled={loadingPreview} className="gap-1.5" data-testid="uc-preview-btn">
                    {loadingPreview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Previsualizar correo real
                </Button>
                <Button size="sm" variant="ghost" onClick={restore} className="gap-1.5 text-slate-600" data-testid="uc-restore-btn">
                    <RotateCcw className="h-3.5 w-3.5" /> Restaurar texto original
                </Button>
                {slug !== 'ultima_capacitacion' && history.length === 0 && (
                    <Button size="sm" variant="ghost" className="gap-1.5 text-red-700" data-testid="uc-delete-btn"
                        onClick={async () => {
                            if (!window.confirm(`¿Eliminar el comunicado "${title || slug}"? Solo es posible si nunca se ha enviado.`)) return;
                            try { await api.delete(`/admin/comunicados/editables/${slug}`); toast.success('Comunicado eliminado'); onDeleted?.(); }
                            catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo eliminar'); }
                        }}>
                        Eliminar este comunicado
                    </Button>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                    <Input placeholder="correo para prueba" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="h-8 w-56 bg-white text-xs" data-testid="uc-test-email-input" />
                    <Button size="sm" variant="outline" onClick={sendTest} disabled={sendingTest} className="gap-1.5" data-testid="uc-send-test-btn">
                        {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Enviar prueba
                    </Button>
                </div>
            </div>

            {testStatus?.last_test && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="uc-test-status">
                    <strong>Última prueba:</strong> {testStatus.last_test.to} · {new Date(testStatus.last_test.at).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ·{' '}
                    <span data-testid="uc-test-events">
                        {testStatus.events?.delivered_at ? '✅ entregado' : '⏳ sin confirmación de entrega'}
                        {' · '}{testStatus.events?.opened_at ? `👁️ abierto (${testStatus.events.opened_count || 1})` : '👁️ sin apertura aún'}
                        {' · '}{testStatus.events?.video_clicked_at ? `▶ clic en el video (${testStatus.events.clicks?.length || 1})` : testStatus.events?.clicked_at ? '🔗 clic registrado' : '▶ sin clic aún'}
                    </span>
                    <span className="block text-[10px] text-sky-700/80 mt-0.5">Se actualiza cada 15 s con los eventos del webhook de Resend{testStatus.tracking_configurado ? '' : ' (webhook aún no configurado)'}.</span>
                </div>
            )}

            {preview && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="uc-preview">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                        <span><strong>Asunto:</strong> {preview.subject}</span>
                        <div className="inline-flex rounded-md border border-slate-300 bg-white overflow-hidden text-[11px]">
                            <button onClick={() => setPreviewDevice('desktop')} className={`px-2.5 py-1 ${previewDevice === 'desktop' ? 'bg-slate-900 text-white' : 'text-slate-600'}`} data-testid="uc-preview-desktop">💻 Escritorio</button>
                            <button onClick={() => setPreviewDevice('mobile')} className={`px-2.5 py-1 ${previewDevice === 'mobile' ? 'bg-slate-900 text-white' : 'text-slate-600'}`} data-testid="uc-preview-mobile">📱 Móvil</button>
                        </div>
                    </div>
                    <iframe
                        title="Vista previa del correo"
                        srcDoc={preview.html}
                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                        className={`mx-auto block bg-white rounded border border-slate-200 ${previewDevice === 'mobile' ? 'w-[380px]' : 'w-full'}`}
                        style={{ height: 900 }}
                        data-testid="uc-preview-iframe"
                    />
                </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="uc-history">
                <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5"><History className="h-4 w-4" /> Historial de envíos de este comunicado</h4>
                    <Button size="sm" variant="ghost" onClick={loadHistory} className="text-xs">Actualizar</Button>
                </div>
                {history.length === 0 ? (
                    <p className="mt-2 text-xs text-slate-500" data-testid="uc-history-empty">Aún no se ha realizado ningún envío.</p>
                ) : (
                    <table className="mt-2 w-full text-xs">
                        <thead className="text-slate-500"><tr><th className="text-left py-1">Fecha (Chile)</th><th className="text-left">Enviado por</th><th className="text-right">Solicitados</th><th className="text-right">Enviados</th><th className="text-right">Fallidos</th><th className="text-right">Omitidos</th><th className="text-right">Abrieron</th><th className="text-right">Clic video</th><th></th></tr></thead>
                        <tbody>
                            {history.map((b, i) => (
                                <React.Fragment key={b.sent_at}>
                                    <tr className="border-t border-slate-100" data-testid={`uc-history-row-${i}`}>
                                        <td className="py-1.5 font-mono">{b.sent_at_cl}</td>
                                        <td className="truncate max-w-[180px]">{b.sent_by}</td>
                                        <td className="text-right font-mono">{b.requested}</td>
                                        <td className="text-right font-mono text-emerald-700">{b.sent}</td>
                                        <td className="text-right font-mono text-red-700">{b.failed}</td>
                                        <td className="text-right font-mono text-slate-500">{b.skipped_already_sent}</td>
                                        <td className="text-right font-mono text-sky-700" data-testid={`uc-history-opened-${i}`}>{b.engagement?.abiertos ?? 0} <span className="text-slate-400">({b.engagement?.pct_abiertos ?? 0}%)</span></td>
                                        <td className="text-right font-mono text-red-700" data-testid={`uc-history-video-${i}`}>{b.engagement?.clics_video ?? 0} <span className="text-slate-400">({b.engagement?.pct_clics_video ?? 0}%)</span></td>
                                        <td className="text-right"><button className="text-blue-700 underline" onClick={() => setOpenBatch(openBatch === i ? null : i)}>{openBatch === i ? 'Ocultar' : 'Ver destinatarios'}</button></td>
                                    </tr>
                                    {openBatch === i && (
                                        <tr><td colSpan={9} className="bg-slate-50 p-2">
                                            <ul className="space-y-0.5">
                                                {b.recipients.map((r) => (
                                                    <li key={r.id || r.email} className="flex justify-between gap-2">
                                                        <span className="truncate">{r.nombre || '—'} · {r.email}</span>
                                                        <span className="flex gap-2 shrink-0">
                                                            {r.ok && r.abierto && <span className="text-sky-700">abrió</span>}
                                                            {r.ok && r.clic_video && <span className="text-red-700">▶ vio el video</span>}
                                                            {r.ok && r.rebotado && <span className="text-amber-700">rebotó</span>}
                                                            <span className={r.ok ? 'text-emerald-700' : 'text-red-700'}>{r.ok ? 'enviado' : `falló: ${r.reason || '-'}`}</span>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </td></tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
                <p className="mt-2 text-[11px] text-slate-500" data-testid="uc-tracking-status">
                    {tracking.configurado
                        ? <>Seguimiento de aperturas/clics activo (webhook Resend verificado) · {tracking.eventos} eventos recibidos. Las aperturas dependen de que el cliente de correo cargue imágenes.</>
                        : <span className="text-amber-700">Seguimiento de aperturas/clics <strong>pendiente de configurar</strong>: activa Open/Click tracking en el dominio de Resend y crea el webhook hacia <code>/api/webhooks/resend</code> con su clave de firma (RESEND_WEBHOOK_SECRET).</span>}
                </p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="uc-engagement">
                <h4 className="text-sm font-semibold text-slate-900">Aperturas y clics por comunicado (todos los enviados)</h4>
                {engagement.length === 0 ? (
                    <p className="mt-2 text-xs text-slate-500">Sin envíos registrados.</p>
                ) : (
                    <table className="mt-2 w-full text-xs">
                        <thead className="text-slate-500"><tr><th className="text-left py-1">Comunicado</th><th className="text-left">Último envío</th><th className="text-right">Enviados</th><th className="text-right">Abrieron</th><th className="text-right">Clics</th><th className="text-right">Clic video</th><th className="text-right">Rebotes</th></tr></thead>
                        <tbody>
                            {engagement.map((t) => (
                                <tr key={t.template_id} className="border-t border-slate-100" data-testid={`uc-engagement-${t.template_id}`}>
                                    <td className="py-1.5 font-mono text-[11px]">{t.template_id}</td>
                                    <td className="font-mono">{t.ultimo_envio_cl || '—'}</td>
                                    <td className="text-right font-mono">{t.enviados}</td>
                                    <td className="text-right font-mono text-sky-700">{t.abiertos} <span className="text-slate-400">({t.pct_abiertos}%)</span></td>
                                    <td className="text-right font-mono">{t.clics}</td>
                                    <td className="text-right font-mono text-red-700">{t.clics_video}</td>
                                    <td className="text-right font-mono text-amber-700">{t.rebotados}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default UltimaCapacitacionEditor;
